import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pmApi } from '../../lib/pmApi';
import { useAuth } from '../../contexts/AuthContext';
import {
  AlertTriangle, Building2, ClipboardList, DollarSign, Home, Plus, Siren,
  CheckCircle2, FileText, Phone, Mail, ShieldAlert, History,
} from 'lucide-react';
import PoActionCenter from '../../components/pm/PoActionCenter';
import PmDashboardAlerts from '../../components/pm/PmDashboardAlerts';

const PM_AM_ROLES = ['property_manager', 'asset_manager'];

const Stat = ({ label, value, accent, testId, danger }) => (
  <div
    data-testid={testId}
    className={`bg-[#141414] border ${danger ? 'border-[#EF4444]/70 bg-[#7F1D1D]/10' : 'border-[#262626]'} rounded-md p-5`}
  >
    <p className={`text-[10px] font-bold tracking-[0.2em] mb-2 ${danger ? 'text-[#EF4444]' : 'text-[#A3A3A3]'}`}>{label}</p>
    <p className={`text-3xl font-['Outfit'] font-bold ${accent || 'text-white'}`}>{value}</p>
  </div>
);

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return s; }
};

const PmDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    pmApi.dashboard().then(setData).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  if (err) return <div data-testid="pm-dashboard-error" className="p-2 text-[#EF4444]">{err}</div>;
  if (!data) return <div className="p-2 text-[#A3A3A3]">Loading…</div>;

  const wo = data.work_orders || {};
  const fin = data.financial || {};
  const props = data.properties || {};
  const nx = data.nexflow_contact || {};
  const recentInvoices = data.recent_invoices || [];
  const recentHistory = data.recent_history || [];
  const pendingApprovals = data.pending_approvals || 0;
  const emergencyOn = (props.emergency_requests || 0) > 0;
  const showFinancials = PM_AM_ROLES.includes(user?.role);

  return (
    <div data-testid="pm-dashboard" className="space-y-8 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">DASHBOARD</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Welcome back, {user?.name?.split(' ')[0] || 'Manager'}</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Here's a snapshot of your portfolio.</p>
        </div>
        <Link to="/pm/submit" data-testid="pm-cta-submit" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-4 py-2.5 rounded-md font-medium hover:bg-[#F3E5AB]">
          <Plus className="w-4 h-4" /> Submit Service Request
        </Link>
      </div>

      {emergencyOn && (
        <div data-testid="pm-emergency-banner" className="bg-[#7F1D1D]/30 border-2 border-[#EF4444] rounded-md p-4 flex items-center gap-3">
          <Siren className="w-6 h-6 text-[#EF4444]" />
          <div className="flex-1">
            <p className="text-white font-medium">{props.emergency_requests} Emergency Request{props.emergency_requests === 1 ? '' : 's'} Open</p>
            <p className="text-[#FCA5A5] text-sm">NexFlow dispatch has been alerted. <Link to="/pm/work-orders?priority=Emergency" className="underline">View open emergencies →</Link></p>
          </div>
        </div>
      )}

      {/* PO Action Center — only renders when PMs have outstanding PO tasks */}
      <PoActionCenter />

      {/* Batch 3 alert cards: Pending PM Approvals · Missing POs · Awaiting Authorization */}
      <PmDashboardAlerts />

      {/* Headline numbers */}
      <div>
        <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-3 uppercase">At A Glance</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat testId="pm-prop-total"  label="Properties"        value={props.total_properties ?? 0} />
          <Stat testId="pm-prop-units"  label="Total Units"       value={props.total_units ?? 0} />
          <Stat testId="pm-prop-open"   label="Open Requests"     value={props.open_requests ?? 0} accent="text-[#D4AF37]" />
          <Stat testId="pm-pending-approvals" label="Pending Approvals" value={pendingApprovals} accent={pendingApprovals > 0 ? 'text-[#D4AF37]' : 'text-white'} />
        </div>
      </div>

      {/* Open Work Orders */}
      <div>
        <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-3 uppercase flex items-center gap-2"><ClipboardList className="w-3.5 h-3.5" /> Open Service Requests</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          <Stat testId="pm-stat-submitted"   label="New"               value={wo.Submitted ?? 0} />
          <Stat testId="pm-stat-scheduled"   label="Scheduled"         value={wo.Scheduled ?? 0} />
          <Stat testId="pm-stat-assigned"    label="Assigned"          value={wo['Technician Assigned'] ?? 0} />
          <Stat testId="pm-stat-enroute"     label="En Route"          value={wo['En Route'] ?? 0} />
          <Stat testId="pm-stat-onsite"      label="On Site"           value={wo['On Site'] ?? 0} />
          <Stat testId="pm-stat-waiting"     label="Waiting Approval"  value={wo['Waiting Approval'] ?? 0} accent="text-[#D4AF37]" />
          <Stat testId="pm-stat-completed"   label="Completed"         value={wo.Completed ?? 0} />
        </div>
      </div>

      {/* Financial — hide from Maintenance Supervisor */}
      {showFinancials && (
        <div>
          <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] mb-3 uppercase flex items-center gap-2"><DollarSign className="w-3.5 h-3.5" /> Financial Summary</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat testId="pm-fin-open"        label="Open Invoices"        value={fin.open_invoices ?? 0} />
            <Stat testId="pm-fin-outstanding" label="Outstanding Balance"  value={fmtMoney(fin.outstanding_balance)} accent="text-[#D4AF37]" />
            <Stat testId="pm-fin-mtd"         label="This Month Spend"     value={fmtMoney(fin.month_to_date_spend)} />
            <Stat testId="pm-fin-ytd"         label="Year-To-Date Spend"   value={fmtMoney(fin.year_to_date_spend)} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent invoices — hide from Maintenance Supervisor */}
        {showFinancials && (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-white font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-[#D4AF37]" /> Recent Invoices</p>
              <Link to="/pm/invoices" className="text-xs text-[#A3A3A3] hover:text-[#D4AF37]">All →</Link>
            </div>
            {recentInvoices.length === 0 ? (
              <p data-testid="pm-recent-invoices-empty" className="text-[#525252] text-sm py-4">No recent invoices.</p>
            ) : (
              <ul className="divide-y divide-[#1f1f1f]" data-testid="pm-recent-invoices">
                {recentInvoices.map((inv) => (
                  <li key={inv.id} className="flex items-center justify-between py-2.5">
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate">#{inv.invoice_number}</p>
                      <p className="text-[#525252] text-xs">{fmtDate(inv.created_at)} · <span className="capitalize">{inv.status}</span></p>
                    </div>
                    <p className="text-[#D4AF37] text-sm font-medium font-['Outfit']">{fmtMoney(inv.total)}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Recent service history */}
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white font-medium flex items-center gap-2"><History className="w-4 h-4 text-[#D4AF37]" /> Recent Service History</p>
            <Link to="/pm/property-history" className="text-xs text-[#A3A3A3] hover:text-[#D4AF37]">All →</Link>
          </div>
          {recentHistory.length === 0 ? (
            <p data-testid="pm-recent-history-empty" className="text-[#525252] text-sm py-4">No completed work yet.</p>
          ) : (
            <ul className="divide-y divide-[#1f1f1f]" data-testid="pm-recent-history">
              {recentHistory.map((r) => (
                <li key={r.id} className="py-2.5">
                  <Link to={`/pm/work-orders/${r.id}`} className="block hover:opacity-90">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-white text-sm truncate flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                          {r.property_name}{r.unit_number ? ` · #${r.unit_number}` : ''}
                        </p>
                        <p className="text-[#525252] text-xs truncate">{r.issue_description}</p>
                      </div>
                      <p className="text-[#525252] text-[10px] uppercase tracking-wider shrink-0">{fmtDate(r.updated_at)}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* NexFlow contact card */}
      <div className="bg-[#141414] border border-[#D4AF37]/30 rounded-md p-5">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-12 h-12 rounded bg-[#D4AF37]/10 items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] mb-1">YOUR NEXFLOW TEAM</p>
            <p className="text-white font-medium">We're on-call for your properties around the clock.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
              {nx.emergency_phone && (
                <a href={`tel:${nx.emergency_phone.replace(/\D/g,'')}`} data-testid="pm-nx-emergency" className="flex items-center gap-2 text-sm text-[#FCA5A5] hover:text-[#EF4444]">
                  <ShieldAlert className="w-3.5 h-3.5" /> Emergency: {nx.emergency_phone}
                </a>
              )}
              {nx.general_phone && (
                <a href={`tel:${nx.general_phone.replace(/\D/g,'')}`} data-testid="pm-nx-phone" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#D4AF37]">
                  <Phone className="w-3.5 h-3.5" /> {nx.general_phone}
                </a>
              )}
              {nx.general_email && (
                <a href={`mailto:${nx.general_email}`} data-testid="pm-nx-email" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#D4AF37]">
                  <Mail className="w-3.5 h-3.5" /> {nx.general_email}
                </a>
              )}
              <Link to="/pm/contacts" data-testid="pm-nx-contacts-link" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#D4AF37]">
                <Home className="w-3.5 h-3.5" /> Full contact directory →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PmDashboard;
